import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

const DemoRequestDropdown = ({
  label,
  name,
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  required = false,
  error,
  className = '',
  labelClassName = '',
  dropdownClassName = '',
  getOptionClassName = () => '',
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleSelect = (option) => {
    onChange({ target: { name, value: option.value } });
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Find the selected option label
  const selectedOption = options.find(option => option.value === value);

  return (
    <div className={`mb-4 ${className}`} ref={dropdownRef}>
      {label && (
        <label 
          htmlFor={name} 
          className={`block text-sm font-medium text-[#121725] mb-1 ${labelClassName}`}
        >
          {label}{required && '*'}
        </label>
      )}
      <div className="relative">
        <button
          type="button"
          className={`w-full h-11 px-3.5 py-2.5 border border-[#e9eaec] rounded-lg shadow-[0px_1px_2px_rgba(16,24,40,0.05)] text-base text-left flex items-center justify-between ${
            error ? 'border-red-500' : ''
          } ${dropdownClassName} ${selectedOption ? getOptionClassName(selectedOption) : ''}`}
          onClick={toggleDropdown}
          {...props}
        >
          <span className={`${!selectedOption ? 'text-[#6a7082]' : ''}`}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 20 20" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
          >
            <path d="M5 7.5L10 12.5L15 7.5" stroke="#6A7082" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
            {options.map((option) => (
              <div
                key={option.value}
                className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${getOptionClassName(option)} ${option.value === value ? 'ring-2 ring-blue-300' : ''}`}
                onClick={() => handleSelect(option)}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
};

DemoRequestDropdown.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.string,
  className: PropTypes.string,
  labelClassName: PropTypes.string,
  dropdownClassName: PropTypes.string,
  getOptionClassName: PropTypes.func,
};

export default DemoRequestDropdown;